import { useMemo, useState } from 'react';

import { AppHeader, MainNav, PullIndicator, Segments } from '../components/AppHeader.jsx';
import { Icon } from '../components/ui/Icon.jsx';
import { ExplanationModal } from '../components/ExplanationModal.jsx';
import { Ring, Bar } from '../components/ui/bits.jsx';
import { usePullToRefresh } from '../hooks.js';
import {
  examCheck,
  getSchoolDays,
  flightStats,
  totalSubConfirmed,
  totalSubDone,
  totalSubPending,
  totalSubs,
} from '../lib/progress.js';
import { dni, fmtDate, fullName } from '../lib/format.js';
import { useSola, solaNastavitve } from '../sola.jsx';
import { t } from '../i18n/index.js';

import { Program } from './student/Program.jsx';
import { Attendance } from './student/Attendance.jsx';
import { FlightLog } from './student/FlightLog.jsx';
import { Documents } from './student/Documents.jsx';
import { ProfileModal } from './student/ProfileModal.jsx';
import { Izpit } from './student/Izpit.jsx';
import { Zasebnost } from './Zasebnost.jsx';
import { FinalTest } from './student/FinalTest.jsx';
import { Ucenje } from './student/Ucenje.jsx';

/*
  Zaslon tečajnika.

  Štirje zavihki — toliko jih palec na dnu telefona zanesljivo zadene —
  vsak s podrazdelki:
    Program   napredek, opomniki, pot do izpita, vaje
    Knjižica  leti | dnevi šolanja
    Učenje    interaktivne vsebine
    Izpit     pogoji in prijava | zaključni test | dokumenti
*/

const TABS = () => [
  { id: 'tecaj', icon: 'list', label: t('Program') },
  { id: 'knjizica', icon: 'plane', label: t('Knjižica') },
  { id: 'ucenje', icon: 'cube', label: t('Učenje') },
  { id: 'izpit', icon: 'award', label: t('Izpit') },
];

export function Student({
  session,
  profile,
  data,
  explanations,
  onRefresh,
  onSignOut,
  /* Predogled: inštruktor gleda skozi oči tečajnika. Samo za gledanje —
     pisanje je zaklenjeno že v sloju api.js. */
  preview = false,
  onExitPreview,
}) {
  const { sola, program, pogoji } = useSola();
  const nastavitve = useMemo(() => solaNastavitve(sola), [sola]);

  const [tab, setTabState] = useState('tecaj');
  const [seg, setSeg] = useState({ knjizica: 'leti', izpit: 'pogoji' });

  /* Ob preklopu zavihka nazaj na vrh. Brez tega pristaneš sredi novega
     razdelka, če si bil v prejšnjem scrollan globoko — na telefonu, kjer je
     navigacija na dnu, je to še posebej moteče. */
  const setTab = (v, s) => {
    setTabState(v);
    if (s) setSeg((x) => ({ ...x, [v]: s }));
    window.scrollTo({ top: 0, behavior: 'instant' });
  };
  const [modal, setModal] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showZasebnost, setShowZasebnost] = useState(false);
  const { distance, busy } = usePullToRefresh(onRefresh, !preview);

  const { progress, flights, attendance, docs, exams } = data;
  const uid = session.user.id;

  const check = useMemo(
    () => examCheck(profile, progress, flights, docs, exams, { program, pogoji }),
    [profile, progress, flights, docs, exams, program, pogoji],
  );

  const days = useMemo(
    () => getSchoolDays(progress, flights, attendance, program),
    [progress, flights, attendance, program],
  );
  const fstats = useMemo(() => flightStats(flights), [flights]);

  const pendingTotal = totalSubPending(program.all, progress);
  const openExplanation = (exId, subIndex, title, subtitle) => {
    const exp = explanations[`${exId}:${subIndex}`];
    if (exp) setModal({ exp, title, subtitle });
  };

  const izpitBadge = (!check.test ? 1 : 0) + (!check.med?.ok ? 1 : 0);

  return (
    <>
      <PullIndicator distance={distance} busy={busy} />

      <a className="skip-link" href="#vsebina">
        {t('Skoči na vsebino')}
      </a>

      {/* Vsebine ob potegu NE premikamo s transformom. Transform ustvari
          nov koordinatni sistem, zato bi bil position: fixed vezan nanj in
          ne na zaslon — spodnji meni bi obvisel na dnu strani namesto na
          dnu zaslona. Povratno informacijo o potegu nosi PullIndicator. */}
      <main className="page">
        {preview ? (
          <PreviewBar profile={profile} onExit={onExitPreview} />
        ) : (
          <AppHeader
            profile={profile}
            role={t('Tečajnik')}
            onSignOut={onSignOut}
            onProfile={() => setShowProfile(true)}
          />
        )}

        <Hero
          profile={profile}
          progress={progress}
          program={program}
          days={days.length}
          pending={pendingTotal}
          leti={fstats.confirmed}
          minLetov={pogoji.min_letov}
        />

        <MainNav
          items={TABS().map((x) => (x.id === 'izpit' && izpitBadge ? { ...x, badge: izpitBadge } : x))}
          value={tab}
          onChange={setTab}
          label={t('Razdelki')}
        />

        <div
          id={`panel-${tab}`}
          role="tabpanel"
          aria-labelledby={`tab-${tab}`}
          tabIndex={-1}
        >
          <span id="vsebina" />
          {tab === 'tecaj' && (
            <>
              {/* Opomniki: zdravniški sodi na ZAČETEK tečaja, test pred
                  višinske lete. Ena kartica, ena vrstica na opomnik —
                  program mora ostati blizu vrha. */}
              <Opomniki check={check} onGo={(seg) => setTab('izpit', seg)} />

              <button
                className="card card--pad row potdo"
                onClick={() => setTab('izpit', 'pogoji')}
                aria-label={t('Pot do izpita')}
              >
                <Icon name={check.ready ? 'award' : 'compass'} size={24} className={check.ready ? 't-ok' : 't-brand'} />
                <span className="spacer stack stack--tight" style={{ textAlign: 'left' }}>
                  <span className="t-md t-bold">{check.ready ? t('Pripravljen na izpit') : t('Pot do izpita')}</span>
                  <Bar done={check.ok} total={check.total} ok={check.ready} thick label={t('Pogoji za izpit: {a} od {b}', { a: check.ok, b: check.total })} />
                </span>
                <span className={`t-lg t-black num ${check.ready ? 't-ok' : 't-brand'}`}>
                  {check.ok}/{check.total}
                </span>
                <Icon name="right" size={18} className="t-dimmer" />
              </button>

              {(nastavitve.povezave.vprasanja || nastavitve.povezave.ucbenik) && (
                <div className="grid-2 quick" style={{ marginBottom: 'var(--s-4)' }}>
                  <QuickLink
                    href={nastavitve.povezave.vprasanja}
                    icon="help"
                    title={t('Teoretična vprašanja')}
                    hint={t('Vadi za teoretični izpit')}
                  />
                  <QuickLink
                    href={nastavitve.povezave.ucbenik}
                    icon="book"
                    title={t('Učbenik')}
                    hint={t('Uradni učbenik v PDF')}
                    arrow="download"
                  />
                </div>
              )}

              <Program
                uid={uid}
                progress={progress}
                explanations={explanations}
                onOpenExplanation={openExplanation}
                onChanged={onRefresh}
                readOnly={preview}
              />
            </>
          )}

          {tab === 'knjizica' && (
            <>
              <Segments
                label={t('Knjižica')}
                value={seg.knjizica}
                onChange={(s) => setSeg((x) => ({ ...x, knjizica: s }))}
                items={[
                  { id: 'leti', icon: 'plane', label: t('Leti'), badge: fstats.pending },
                  { id: 'dnevi', icon: 'calendar', label: t('Dnevi') },
                ]}
              />
              {seg.knjizica === 'leti' ? (
                <FlightLog
                  uid={uid}
                  profile={profile}
                  flights={flights}
                  onChanged={onRefresh}
                  readOnly={preview}
                />
              ) : (
                <Attendance uid={uid} days={days} onChanged={onRefresh} readOnly={preview} />
              )}
            </>
          )}

          {tab === 'ucenje' && (
            <Ucenje uid={uid} ucenje={data.ucenje ?? []} onChanged={onRefresh} readOnly={preview} />
          )}

          {tab === 'izpit' && (
            <>
              <Segments
                label={t('Izpit')}
                value={seg.izpit}
                onChange={(s) => setSeg((x) => ({ ...x, izpit: s }))}
                items={[
                  { id: 'pogoji', icon: 'compass', label: t('Pogoji'), badge: !check.med?.ok ? 1 : 0 },
                  { id: 'test', icon: 'clipboard', label: t('Test'), badge: !check.test ? 1 : 0 },
                  { id: 'dokumenti', icon: 'idCard', label: t('Dokumenti') },
                ]}
              />
              {seg.izpit === 'pogoji' && <Izpit check={check} onGoDocs={() => setTab('izpit', 'dokumenti')} />}
              {seg.izpit === 'test' && (
                <FinalTest
                  profile={profile}
                  attempts={exams}
                  onChanged={onRefresh}
                  readOnly={preview}
                />
              )}
              {seg.izpit === 'dokumenti' && (
                <Documents uid={uid} docs={docs} onChanged={onRefresh} readOnly={preview} />
              )}
            </>
          )}
        </div>
      </main>

      {showProfile && !preview && (
        <ProfileModal
          session={session}
          profile={profile}
          data={data}
          onClose={() => setShowProfile(false)}
          onSaved={onRefresh}
          onOpenZasebnost={() => setShowZasebnost(true)}
        />
      )}

      {showZasebnost && <Zasebnost onClose={() => setShowZasebnost(false)} />}

      {modal && (
        <ExplanationModal
          title={modal.title}
          subtitle={modal.subtitle}
          exp={modal.exp}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

/*
  Povzetek na vrhu.

  Namenoma NE pove „naslednji korak“ — šola dela po vremenu in po dnevih,
  ne po zaporedju vaj (odločitev je zapisana v CLAUDE.md). Pove samo, kje
  tečajnik je.
*/
function Hero({ profile, progress, program, days, pending, leti, minLetov }) {
  const confirmed = totalSubConfirmed(program.all, progress);
  const done = totalSubDone(program.all, progress);
  const total = totalSubs(program.all);

  return (
    <section className="card card--brand card--pad hero" aria-label={t('Povzetek napredka')}>
      <div className="row" style={{ gap: 'var(--s-5)' }}>
        <Ring done={confirmed} total={total} size={84} caption={t('potrjeno')} />

        <div className="spacer stack stack--tight">
          <h1 className="t-lg">
            {t('Živjo,')} {profile?.ime}
            {profile?.stevilka != null && (
              <span className="t-sm t-brand t-bold num">  · {t('št.')} {profile.stevilka}</span>
            )}
          </h1>

          <Bar done={confirmed} total={total} thick label={t('Potrjenih vaj: {a} od {b}', { a: confirmed, b: total })} />

          <div className="row row--wrap t-sm t-dim" style={{ gap: 'var(--s-3)' }}>
            <span className="num">
              <strong className="t-ok">{confirmed}</strong> {t('potrjenih')}
            </span>
            {pending > 0 && (
              <span className="num">
                <strong className="t-wait">{pending}</strong> {t('čaka inštruktorja')}
              </span>
            )}
            <span className="num">
              <strong>{leti}</strong>/{minLetov} {t('letov')}
            </span>
            <span className="num">
              <strong>{days}</strong> {dni(days)} {t('šolanja')}
            </span>
          </div>

          {done > confirmed && pending === 0 && (
            <p className="t-xs t-dimmer">
              {t('{n} odkljukanih vaj še ni potrjenih.', { n: done - confirmed })}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

/* Opomniki na vrhu Programa: kar tečajnik mora urediti, preden leti. */
function Opomniki({ check, onGo }) {
  const items = [];
  if (!check.med?.ok) {
    items.push({
      key: 'med',
      seg: 'pogoji',
      tone: 'bad',
      icon: 'alert',
      title:
        check.med?.uploaded && !check.med?.examined
          ? t('Zdravniški pregled čaka na potrditev inštruktorja')
          : check.med?.examined && !check.med?.valid
            ? t('Zdravniški pregled je potekel')
            : t('Opravi zdravniški pregled za B kategorijo'),
      hint: t('Opravi ga na začetku tečaja, ne pred izpitom. Brez njega ne smeš leteti.'),
    });
  }
  if (!check.test) {
    items.push({
      key: 'test',
      seg: 'test',
      tone: 'brand',
      icon: 'clipboard',
      title: t('Zaključni test še ni opravljen'),
      hint: t('Rešiti ga moraš pred višinskimi leti.'),
    });
  }
  if (!items.length) return null;
  return (
    <section className="card opomniki" aria-label={t('Opomniki')} style={{ marginBottom: 'var(--s-4)' }}>
      {items.map((it) => (
        <button key={it.key} className={`opomnik opomnik--${it.tone}`} onClick={() => onGo(it.seg)}>
          <Icon name={it.icon} size={22} className={`t-${it.tone}`} />
          <span className="spacer">
            <span className={`t-md t-bold t-${it.tone}`} style={{ display: 'block' }}>
              {it.title}
            </span>
            <span className="t-sm t-dim">{it.hint}</span>
          </span>
          <Icon name="right" size={18} className={`t-${it.tone}`} />
        </button>
      ))}
    </section>
  );
}

function QuickLink({ href, icon, title, hint, arrow = 'right' }) {
  if (!href) return null;
  return (
    <a
      className="card card--pad row"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit', padding: 'var(--s-3) var(--s-4)' }}
    >
      <Icon name={icon} size={22} className="t-brand" />
      <span className="spacer">
        <span className="t-md t-bold" style={{ display: 'block' }}>
          {title}
        </span>
        <span className="t-xs t-dim">{hint}</span>
      </span>
      <Icon name={arrow} size={18} className="t-dimmer" />
    </a>
  );
}

/*
  Pas nad predogledom.

  Mora biti očiten in ves čas viden: inštruktor mora v vsakem trenutku
  vedeti, da gleda tuj profil in da tu ničesar ne spreminja.
*/
function PreviewBar({ profile, onExit }) {
  return (
    <div
      className="appbar"
      style={{
        background: 'var(--brand-wash)',
        borderBottom: '1px solid var(--brand-border)',
      }}
    >
      <button className="btn-icon" onClick={onExit} aria-label={t('Zapri predogled')}>
        <Icon name="left" size={20} />
      </button>

      <span className="spacer" style={{ minWidth: 0 }}>
        <span className="t-xs eyebrow" style={{ display: 'block' }}>
          {t('Predogled — samo za gledanje')}
        </span>
        <span className="t-md t-bold truncate" style={{ display: 'block' }}>
          {fullName(profile) ?? t('Tečajnik')}
          {profile?.stevilka != null && (
            <span className="t-brand num"> · {t('št.')} {profile.stevilka}</span>
          )}
          {profile?.datum_tecaja && (
            <span className="t-sm t-dim"> · {t('tečaj od')} {fmtDate(profile.datum_tecaja)}</span>
          )}
        </span>
      </span>

      <button className="btn btn--sm btn--secondary" onClick={onExit}>
        {t('Nazaj v admin')}
      </button>
    </div>
  );
}
